import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useParams, useLocation } from 'react-router-dom';

import CurrentStatus from './current-status';
import LeftNavBundleItem from './left-nav-bundle-item';
import TotalBundleStats from './left-nav-total-bundle';
import UserNavigationMenu from '../settings/user-navigation-menu';

import { useDashboardDataContext } from '~/client/components/project/dashboard/dashboard-context';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import Spacer, { spc } from '~c/components/base/spacer';
import { useAssessmentFromId } from '~c/hooks/assessment';
import { useConditionalControls } from '~c/hooks/conditional-controls';
import {
  useAssessmentHelpers,
  useAssessmentRoutePrefix,
  dashboardRouteUtil,
  useDashboardHandlers,
} from '~c/routes';
interface Props {
  className?: string;
}

const AssessmentLeftNavImpl: React.FC<Props> = ({ className }) => {
  const {
    projectId,
    candidateId,
    functionalModelId,
    assessmentId,
    agentId,
    vendorModelId,
    vendorAgentId,
  } = useParams();
  const { state } = useLeftNavContext();
  const location = useLocation();
  const { navigateToMainDashboard } = useDashboardHandlers();
  const { project } = useDashboardDataContext();
  const dashboardHelper = dashboardRouteUtil.useHelper();
  const effectiveProjectId =
    vendorModelId ??
    vendorAgentId ??
    candidateId ??
    agentId ??
    functionalModelId ??
    projectId;
  const {
    aiSystem,
    functionalModel,
    candidateModel,
    candidateAgent,
    vendorModel,
    vendorAgent,
  } = useAssessmentHelpers();

  // Determine which set of route utilities to use based on the URL params
  const { matchRoute, navigateTo } = React.useMemo(() => {
    if (vendorModelId) {
      return vendorModel.assessment;
    }
    if (vendorAgentId) {
      return vendorAgent.assessment;
    }
    if (candidateId) {
      return candidateModel.assessment;
    }
    if (functionalModelId) {
      return functionalModel.assessment;
    }
    if (agentId) {
      return candidateAgent.assessment;
    }
    return aiSystem.assessment;
  }, [
    vendorModelId,
    vendorAgentId,
    candidateId,
    functionalModelId,
    aiSystem,
    functionalModel,
    candidateModel,
    candidateAgent,
    vendorModel,
    vendorAgent,
    agentId,
  ]);

  const { matchRoute: bundleMatchRoute, navigateTo: bundleNavigateTo } =
    React.useMemo(() => {
      if (vendorModelId) {
        return vendorModel.bundle;
      }
      if (vendorAgentId) {
        return vendorAgent.bundle;
      }
      if (candidateId) {
        return candidateModel.bundle;
      }
      if (agentId) {
        return candidateAgent.bundle;
      }
      if (functionalModelId) {
        return functionalModel.bundle;
      }
      return aiSystem.bundle;
    }, [
      vendorModelId,
      vendorAgentId,
      candidateId,
      functionalModelId,
      aiSystem,
      functionalModel,
      candidateModel,
      agentId,
      candidateAgent,
      vendorModel,
      vendorAgent,
    ]);

  const { matchRoute: bundleVersionMatchRoute } = React.useMemo(() => {
    if (vendorModelId) {
      return vendorModel.version;
    }
    if (vendorAgentId) {
      return vendorAgent.version;
    }
    if (candidateId) {
      return candidateModel.version;
    }
    if (functionalModelId) {
      return functionalModel.version;
    }
    if (agentId) {
      return candidateAgent.version;
    }
    return aiSystem.version;
  }, [
    vendorModelId,
    vendorAgentId,
    candidateId,
    functionalModelId,
    aiSystem,
    functionalModel,
    candidateModel,
    agentId,
    candidateAgent,
    vendorModel,
    vendorAgent,
  ]);

  const { assessment, loading, bundleHelper } = useAssessmentFromId(
    effectiveProjectId,
    assessmentId
  );

  const { visibleControls } = useConditionalControls({
    bundleHelper,
    screeningVisibility: (project as any)?.screeningVisibility ?? null,
  });

  const controlBundleList = assessment?.policy?.controlBundleList ?? [];

  const getRoutePrefix = useAssessmentRoutePrefix();

  const name = getRoutePrefix(
    bundleMatchRoute('BUNDLE_FORM'),
    bundleVersionMatchRoute('VERSIONS_IDS'),
    candidateId,
    functionalModelId,
    agentId,
    vendorModelId,
    vendorAgentId
  );

  let welcome: React.ReactNode = null;

  if (visibleControls.size === 0) {
    return null;
  }

  if (assessment?.completionPercent === 0) {
    welcome = (
      <LeftNavMenuItem
        icon={HelpOutlineOutlinedIcon}
        selected={matchRoute('GETTING_STARTED')}
        onClick={() => navigateTo('GETTING_STARTED')}
        className="getting-started-item"
      >
        Getting started
      </LeftNavMenuItem>
    );
  }

  // Handle back navigation based on source
  const handleBackNavigation = () => {
    // Check if we came from tasks page via location state
    if (location.state && location.state.from === 'tasks') {
      return dashboardHelper.navigateTo('TASKS');
    }
    // Go back to main dashboard (includes projectType for correct route)
    return navigateToMainDashboard(
      candidateId,
      functionalModelId,
      undefined, // params
      vendorModelId,
      vendorAgentId,
      project?.projectType
    );
  };

  return (
    <LeftNavMenu
      className={className}
      name={name}
      header={{
        showBackButton: true,
        loading,
        onClick: handleBackNavigation,
      }}
    >
      <CurrentStatus
        policyName={assessment?.policy?.name}
        completionPercent={assessment?.completionPercent}
        assessmentId={assessmentId}
        className="current-status"
      />

      {/* {welcome} */}

      {controlBundleList.map((bundle) => (
        <LeftNavMenuItem
          key={bundle._id}
          selected={bundleMatchRoute('BUNDLE_FORM', { bundleId: bundle._id })}
          onClick={() => {
            return bundleNavigateTo('BUNDLE_FORM', {
              params: {
                projectId: projectId ?? '',
                functionalModelId: functionalModelId ?? '',
                candidateId: candidateId ?? '',
                agentId: agentId ?? '',
                vendorModelId: vendorModelId ?? '',
                vendorAgentId: vendorAgentId ?? '',
                bundleId: bundle._id,
              },
            });
          }}
        >
          <LeftNavBundleItem
            controlBundle={bundle}
            isActive={bundleMatchRoute('BUNDLE_FORM', { bundleId: bundle._id })}
          />
        </LeftNavMenuItem>
      ))}
      <div className="divider" />
      <TotalBundleStats
        controlBundleList={controlBundleList}
        className={className}
      />
      <Spacer className="grow" />
      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

export const AssessmentLeftNav = muiStyled(AssessmentLeftNavImpl)(
  ({ theme }) => css`
    .grow {
      flex-grow: 1;
    }
    .userinfo {
      padding: 16px;
      text-transform: capitalize;
    }
    .task-menu-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
    .task-badge {
      background-color: ${theme.palette.error.main};
      color: ${theme.palette.error.contrastText};
      border-radius: ${theme.shape.borderRadius}px;
      padding: 2px 8px;
      font-size: 12px;
      margin-left: 8px;
      font-weight: 500;
    }

    .v2-header {
      margin-left: -10px;
    }

    .getting-started-item {
      margin-bottom: ${spc.xxs}px;
    }

    .current-status {
      margin-top: ${spc.xxs}px;
      margin-bottom: ${spc.xxs}px;
    }

    .divider {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding: 0px 16px;
      width: 260px;
      height: 1px;
      background-color: ${theme.palette.divider};
      align-self: stretch;
    }
  `
);

export default AssessmentLeftNav;
